import {
  CropIcon,
  ElectricalServicesIcon,
  PersonOutlineIcon,
} from "../../dx-icons";

import { SpecRow, type SpecRowProps } from "./spec-row";

/**
 * SpecRow に表示する RoomType feature の slug 許可リスト。
 * 順序は `feature.sort` に従う。
 */
const ALLOWED_FEATURE_SLUGS = new Set([
  "onsen",
  "sauna",
  "dog-friendly",
  "dog-run",
  "display",
]);

type RoomTypeFeatureLite = {
  slug: string;
  displayName: string;
  iconSlug?: string | null;
  sort: number;
};

type SpecListProps = {
  capacity?: string | null;
  roomSize?: string | null;
  indoorEquipment?: string | null;
  /** RoomType.features。許可 slug のみ描画する */
  features: ReadonlyArray<RoomTypeFeatureLite>;
};

/**
 * 部屋詳細のスペックリスト。SpecRow を並べて表示する。
 * - 上段は固定スペック (収容人数 / 広さ / 室内設備)
 * - 下段は RoomType の features を allow-list で絞って sort 順に並べる
 * - 各 feature の icon は `iconSlug` を `/i/dx/<iconSlug>.svg` にマップする
 *   (iconSlug が無ければ space を保つ)
 * - 区切り線は SpecList の関心事
 */
export function SpecList({
  capacity,
  roomSize,
  indoorEquipment,
  features,
}: SpecListProps) {
  const fixedRows: Array<{ key: string; props: SpecRowProps } | null> = [
    capacity
      ? {
          key: "capacity",
          props: { icon: <PersonOutlineIcon />, title: capacity },
        }
      : null,
    roomSize
      ? { key: "roomSize", props: { icon: <CropIcon />, title: roomSize } }
      : null,
    indoorEquipment
      ? {
          key: "indoorEquipment",
          props: { icon: <ElectricalServicesIcon />, title: indoorEquipment },
        }
      : null,
  ];

  const featureRows = features
    .filter((f) => ALLOWED_FEATURE_SLUGS.has(f.slug))
    .slice()
    .sort((a, b) => a.sort - b.sort)
    .map((f) => ({
      key: `feature-${f.slug}`,
      props: {
        icon: <FeatureIcon iconSlug={f.iconSlug} />,
        title: f.displayName,
      } satisfies SpecRowProps,
    }));

  const rows = [...fixedRows, ...featureRows].filter(
    (r): r is { key: string; props: SpecRowProps } => r !== null,
  );

  if (rows.length === 0) return null;
  return (
    <ul className="flex flex-col pt-[8px] pb-[16px]">
      {rows.map((row, i) => (
        <li key={row.key}>
          {i > 0 && <SpecDivider />}
          <SpecRow {...row.props} />
        </li>
      ))}
    </ul>
  );
}

/**
 * `iconSlug` をそのまま SVG の URL として扱い、mask-image で着色するアイコン。
 * `bg-current` + `text-*` で SpecRow の text 色を継承する。
 * iconSlug が null のときは透明な 20×20 を返してレイアウト幅を保つ。
 */
function FeatureIcon({ iconSlug }: { iconSlug?: string | null }) {
  if (!iconSlug) return <span aria-hidden className="block size-[20px]" />;
  return (
    <span
      aria-hidden
      className="block size-[20px] shrink-0 bg-current"
      style={{
        WebkitMaskImage: `url(${iconSlug})`,
        maskImage: `url(${iconSlug})`,
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskPosition: "center",
        maskPosition: "center",
      }}
    />
  );
}

function SpecDivider() {
  return (
    <div className="px-[16px]">
      <div className="bg-dx-border-subtle h-px" />
    </div>
  );
}

/**
 * ロード中の placeholder。実体と同じ padding / 区切り線で行を並べる。
 * 行数は実データの平均的な数 (capacity / 広さ / 室内設備 / 特徴) を見越して 4 行。
 */
export function SpecListSkeleton({ rows = 4 }: { rows?: number } = {}) {
  return (
    <ul className="flex flex-col pt-[8px] pb-[16px]">
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i}>
          {i > 0 && <SpecDivider />}
          <div className="flex items-start gap-[12px] px-[16px] py-[10px]">
            <div className="bg-dx-surface-subtle size-[20px] shrink-0 animate-pulse rounded-sm" />
            <div className="bg-dx-surface-subtle h-[20px] flex-1 animate-pulse rounded-sm" />
          </div>
        </li>
      ))}
    </ul>
  );
}
